import * as React from "react";

import clsx from "classnames";
import { useSelector } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import MuiTab from "@material-ui/core/Tab";
import MuiTabs from "@material-ui/core/Tabs";
import MuiButton from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";

import ActionAdd from "@material-ui/icons/Add";
import ActionDelete from "@material-ui/icons/Delete";
import ActionPrint from "@material-ui/icons/Print";
import EditIcon from "@material-ui/icons/Edit";
import HistoryIcon from "@material-ui/icons/History";
import LinkIcon from "mdi-material-ui/Link";
import FileIcon from "@material-ui/icons/InsertDriveFile";

import {
  PageHeader,
  PageFooter,
  SimpleShowLayout,
  ReferenceInput,
  AutoCompleteInput,
  SelectInput,
  TextInput,
  TextField,
  ReferenceField,
  FunctionField,
  NumberInput,
  BackButton,
  DeleteButton,
  Button,
  required,
  maxValue,
  minValue,
  regex
} from "jazasoft";

// local components
import MuiTable from "../../../components/MuiTable";
import CardHeader from "../../../components/CardHeader";
import FormDialog from "../../../components/FormDialog";
import HistoryDialog from "../../../components/HistoryDialog";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Tabs from "../../../components/tab/Tabs";
import SubTab from "../../../components/tab/SubTab";

// helpers
import { inputOptions, fieldOptions, isEmpty, listFromCsv } from "../../../utils/helpers";
import { Role, EstimationState, EstimationStateValue, CSV, FabricDirection } from "../../../utils/types";
import { REGEX_REPEAT, REGEX_SHRINKAGE } from "../../../utils/regex";
import hasRole from "../../../utils/hasRole";
import hasPrivilege from "../../../utils/hasPrivilege";

import useEstimationView from "./useEstimationView";
import { useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { CUT_OFF_SIZE_RATIO, DEFAULT_SEPARATOR, PART_SHELL } from "../../../constants";
import SizeBreakupDrawer from "./SizeBreakupDrawer";

const choicesCutPlanType = [
  { id: "ALGORITHMIC", name: "Algorithmic Plan" },
  { id: "CUSTOM", name: "Custom Plan" }
];

const getFooterActions = ({ cutPlanType, state, isAdmin, isCutPlanner, isCad, rows: markers }) => {
  let footerActions = [];
  if ((isAdmin || isCutPlanner || isCad) && EstimationStateValue[state] <= 2) {
    if (markers.length > 0) {
      footerActions.push({ name: "miniMarkerUpload", tooltip: "Upload", options: { color: "primary" } });
    }
    if (isEmpty(cutPlanType)) {
      footerActions.push({ name: "setting", tooltip: "Setting", options: { color: "primary" } });
    }

    if (cutPlanType === "ALGORITHMIC") {
      if (markers.length === 0) {
        footerActions.push({ name: "run", tooltip: "Run Cut Plan", options: { color: "primary" } });
      } else {
        footerActions.push({ name: "delete", tooltip: "Delete Cut Plan", options: { color: "primary" } });
      }
    }
  }
  return footerActions;
};

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(3),
    paddingTop: 0
  },
  card: {
    padding: 0,
    boxShadow: theme.shadows[1],
    backgroundColor: "white"
  },
  padding: {
    padding: "0.5em 1.5em"
  },
  margin: {
    margin: "1.5em"
  },
  mrTop: {
    marginTop: "1.5em"
  },
  marginVertical: {
    marginTop: "1.5em",
    marginBottom: "1.5em"
  },
  headerCell: {
    padding: "12px 18px",
    fontSize: 10,
    "&:last-child": {
      padding: "12px  18px",
      fontSize: 10
    }
  },
  rowCell: {
    padding: "12px  18px",
    "&:last-child": {
      padding: "12px  18px"
    }
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.5rem"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  pageFooter: {
    "&>*": {
      marginRight: "1.5em"
    }
  }
}));

const EstimationView = props => {
  const classes = useStyles();
  const { id, roles = [], hasAccess } = props;

  const {
    DialogName,
    estimation,
    columnsPart,
    rowsPart,
    partList,
    flowList,
    flowIdx,
    summaryIdx,
    activeFlow,
    revIdx,
    columnsSummary,
    rowsSummaryMap,
    columnsSizeBreakup,
    rowsSizeBreakup,
    rowsMarker,
    dialogState,
    openFinalizeDialog,
    manuallyFinalizedRevNo,
    choicesRevisions,
    choicesTechSheets,
    onCloseDialog,
    getColumnsMarker,
    onFlowChange,
    setRevIdx,
    setSummaryIdx,
    setDialogState,
    setOpenFinalizeDialog,
    onFinalizeDialogClose,
    onFinalizeSubmit,
    onEstimationUpdate,
    onPartSubmit,
    onSizeBreakupSubmit,
    onMarkerSubmit,
    onFooterActionClick,
    onInlineActionClick,
    updateRevisionSetting,
    onRevision,
    onRevisionDelete,
    onPrint,
    onDownloadTechSheet,
    onDeleteMiniMarker,
    isAttachmentUpload,
    onDrawerSubmit,
    setDrawerState,
    drawerState
  } = useEstimationView(props);

  const revReasonVal = useSetting(SettingKeys.ESTIMATION_REASONS);
  const choicesRevReasons = listFromCsv(revReasonVal).map(e => ({ id: e, name: e }));

  const formValues = useSelector(state => state.form && state.form["record-form"]?.values);

  const isMerchant = hasRole(roles, [Role.MERCHANT]);
  const isAdmin = hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER]);
  const isCutPlanner = hasRole(roles, [Role.CUT_PLANNER]);
  const isEstCad = hasRole(roles, [Role.EST_CAD]);
  const isCad = isEstCad || hasRole(roles, Role.CAD);

  // Disable Size Breakup if any markers for  selected stage and selected flow has been generated
  const isSizeBreakupEditable = rowsMarker.length === 0;

  let inlineActions = [];
  if (isAdmin || isCutPlanner) {
    inlineActions = [{ name: "link", tooltip: "Link Costing", icon: <LinkIcon /> }];
  }

  const onRevisionClick = () => {
    setDialogState({ active: true, name: DialogName.REVISION });
  };

  const onClose = () => {
    dialogState.onActionComplete && dialogState.onActionComplete([]);
    setDialogState(prev => ({ ...prev, active: false }));
  };

  const dialogTitle =
    dialogState.name &&
    dialogState.name.charAt(0).toUpperCase() +
      dialogState.name
        .slice(1)
        .replace(/([A-Z])/g, " $1")
        .trim();

  const revisionCount = activeFlow?.revisionCount || 1;
  const revisionList = Array(revisionCount)
    .fill("")
    .map((_, idx) => idx + 1);

  const summaryPart = rowsPart[summaryIdx];

  const onUpdateSizeBreakup = (event, action, rows, filterValues, onActionComplete) => {
    setDrawerState({ open: true, rows: rowsSizeBreakup, columns: columnsSizeBreakup, onActionComplete });
  };

  const footerActions = [
    { name: "Update", tooltip: "Update", showLabel: true, icon: <EditIcon />, options: { variant: "outlined", color: "primary" } }
  ];

  const onCloseDrawer = () => {
    drawerState.onActionComplete && drawerState.onActionComplete(drawerState.rows);
    setDrawerState({ open: false, rows: [], columns: [] });
  };

  return (
    <div>
      <PageHeader title={`Estimation #${id}`} />
      <SizeBreakupDrawer
        open={drawerState.open}
        rows={drawerState.rows}
        columns={drawerState.columns}
        onClose={onCloseDrawer}
        onSubmit={onDrawerSubmit}
        onActionComplete={drawerState.onActionComplete}
      />

      <FormDialog
        maxWidth={dialogState.name === DialogName.SETTINGS ? "md" : "sm"}
        title={dialogTitle}
        open={
          (dialogState.active &&
            (dialogState.name === DialogName.REVISION ||
              dialogState.name === DialogName.SETTINGS ||
              dialogState.name === DialogName.STYLE_DETAILS ||
              dialogState.name === DialogName.LINK_COSTING)) ||
          false
        }
        record={{
          ...estimation,
          ...dialogState.revisionDetail,
          ...dialogState.revisionDetail?.cutSetting,
          costingId: dialogState.estPart?.costingId,
          discardedWays: "3,5,7,9"
        }}
        onSubmit={
          dialogState.name === DialogName.REVISION
            ? onRevision
            : dialogState.name === DialogName.SETTINGS
              ? updateRevisionSetting
              : onEstimationUpdate
        }
        onClose={onClose}>
        {dialogState.name === DialogName.REVISION && (
          <SelectInput source="reason" label="Revision Reason" validate={[required()]} choices={choicesRevReasons} {...inputOptions(12)} />
        )}
        {dialogState.name === DialogName.REVISION && <TextInput source="remarks" label="Revision Remarks" {...inputOptions(12)} />}

        {dialogState.name === DialogName.SETTINGS && (
          <SelectInput
            source="cutPlanType"
            label="CutPlan Type"
            validate={[required()]}
            disabled={dialogState.revisionDetail?.state !== EstimationState.NEW}
            choices={dialogState.isSizeRatio ? choicesCutPlanType.filter(e => e.id === "CUSTOM") : choicesCutPlanType}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && !dialogState.isSizeRatio && formValues?.cutPlanType === "ALGORITHMIC" && (
          <NumberInput
            source="maxPieces"
            label="Max Pieces"
            disabled={dialogState.revisionDetail?.state !== EstimationState.NEW && dialogState.revisionDetail?.state !== EstimationState.PENDING}
            validate={[required(), minValue(1), maxValue(50)]}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && !dialogState.isSizeRatio && formValues?.cutPlanType === "ALGORITHMIC" && (
          <TextInput
            source="discardedWays"
            disabled={dialogState.revisionDetail?.state !== EstimationState.NEW && dialogState.revisionDetail?.state !== EstimationState.PENDING}
            validate={[required()]}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && (
          <NumberInput source="width" disabled={EstimationStateValue[dialogState.revisionDetail?.state] >= 2} {...inputOptions(6)} />
        )}
        {dialogState.name === DialogName.SETTINGS && (
          <SelectInput
            source="csv"
            label="CSV"
            disabled={EstimationStateValue[dialogState.revisionDetail?.state] >= 2}
            choices={Object.keys(CSV).map(key => ({ id: CSV[key], name: CSV[key] }))}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && (
          <TextInput
            source="repeat"
            validate={[regex(REGEX_REPEAT, "Invalid Repeat. e.g - 1.5 X 2.5 or 1.5")]}
            disabled={EstimationStateValue[dialogState.revisionDetail?.state] >= 2}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && (
          <TextInput
            source="shrinkage"
            validate={[regex(REGEX_SHRINKAGE, "Invalid shrinkage. e.g - L1.5% W2.5%")]}
            disabled={EstimationStateValue[dialogState.revisionDetail?.state] >= 2}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && (
          <SelectInput
            source="fabDirection"
            label="Fabric Direction"
            disabled={EstimationStateValue[dialogState.revisionDetail?.state] >= 2}
            choices={Object.keys(FabricDirection).map(key => ({ id: FabricDirection[key], name: FabricDirection[key] }))}
            {...inputOptions(6)}
          />
        )}
        {dialogState.name === DialogName.SETTINGS && (
          <TextInput
            source="matchingParameter"
            label="Matching Parameter"
            disabled={EstimationStateValue[dialogState.revisionDetail?.state] >= 2}
            {...inputOptions(6)}
          />
        )}

        {dialogState.name === DialogName.STYLE_DETAILS && (
          <TextInput label="PO Ref" source="poRef" disabled={roles.includes(Role.CUT_PLANNER)} {...inputOptions(6)} />
        )}
        {dialogState.name === DialogName.STYLE_DETAILS && (
          <TextInput label="Customer PO Ref" source="customerPoRef" disabled={roles.includes(Role.CUT_PLANNER)} {...inputOptions(6)} />
        )}
        {dialogState.name === DialogName.STYLE_DETAILS && (isAdmin || isMerchant) && (
          <SelectInput allowEmpty source="techSheet" choices={choicesTechSheets} {...inputOptions(6)} />
        )}
        {dialogState.name === DialogName.STYLE_DETAILS && <TextInput source="fit" validate={[required()]} {...inputOptions(6)} />}
        {dialogState.name === DialogName.STYLE_DETAILS && <TextInput source="styleDesc" {...inputOptions(6)} />}
        {dialogState.name === DialogName.STYLE_DETAILS && <TextInput source="remarks" {...inputOptions(6)} />}

        {dialogState.name === DialogName.LINK_COSTING && (
          <ReferenceInput label="Costing" source="costingId" reference="costings" searchKeys={["cstId", "styles"]} allowEmpty {...inputOptions(12)}>
            <AutoCompleteInput optionText={record => `${record.cstId}, ${record.styles || ""}`} disablePortal={true} />
          </ReferenceInput>
        )}
      </FormDialog>
      <FormDialog
        title="Finalize Estimation"
        record={{ revisionNo: manuallyFinalizedRevNo }}
        open={openFinalizeDialog}
        onSubmit={onFinalizeSubmit}
        onClose={onFinalizeDialogClose}>
        <SelectInput source="revisionNo" label="Estimation Revisions" choices={choicesRevisions} {...inputOptions(12)} />
      </FormDialog>

      <HistoryDialog
        open={dialogState.name === DialogName.HISTORY && dialogState.active}
        url={`estimations/${props.id}/logs`}
        onClose={() => setDialogState({})}
      />

      <ConfirmDialog
        label="Delete"
        confirmTitle="Delete Mini Marker?"
        open={dialogState.name === DialogName.MINI_MARKER_DELETE && dialogState.active}
        onCancel={onCloseDialog}
        onConfirm={onDeleteMiniMarker}
      />
      {Object.keys(estimation).length > 0 && (
        <div className={classes.content}>
          {/**Style Details */}
          <Card>
            <CardHeader title="Style Details" />
            <Divider />
            <div>
              <SimpleShowLayout record={estimation} footer={false} i18nKey="estimations" style={{ margin: "1.5em" }}>
                <TextField source="poRef" {...fieldOptions(3)} />
                <TextField source="customerPoRef" {...fieldOptions(3)} />
                <TextField label="Est. Id" source="estId" {...fieldOptions(3)} />
                <ReferenceField
                  label="Checklist Id"
                  source="cuChecklistId"
                  reference="cuChecklists"
                  linkType={(record = {}) => `/estimation/cuChecklists/${record.cuChecklistId}/view`}
                  {...fieldOptions(3)}>
                  <TextField source="checklistId" />
                </ReferenceField>
                <TextField source="fit" {...fieldOptions(3)} />
                <TextField label="Season" source="season.name" {...fieldOptions(3)} />
                <TextField label="Customer" source="customer.name" {...fieldOptions(3)} />
                <TextField label="Product" source="product.name" {...fieldOptions(3)} />
                <TextField source="style" {...fieldOptions(3)} />
                <FunctionField
                  label="Tech Sheet"
                  render={record =>
                    record?.techSheet?.length > 0 ? (
                      <Chip
                        label={record.techSheet}
                        variant="outlined"
                        icon={<FileIcon />}
                        component="a"
                        clickable
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => onDownloadTechSheet(record.techSheet)}
                      />
                    ) : (
                      "-"
                    )
                  }
                  {...fieldOptions(3)}
                />
                <TextField source="styleDesc" label="Style Description" {...fieldOptions(6)} />
                <TextField source="remarks" label="Remarks" {...fieldOptions(6)} />
              </SimpleShowLayout>
              <Divider />
              {(isAdmin || isMerchant || isCutPlanner) && (
                <div className={classes.footer}>
                  <MuiButton color="primary" onClick={() => setDialogState({ active: true, name: DialogName.STYLE_DETAILS })}>
                    Edit
                  </MuiButton>
                </div>
              )}
            </div>
          </Card>

          <div className={clsx(classes.mrTop, { [classes.card]: false })}>
            {flowList.length > 1 && (
              <Tabs value={flowIdx} onChange={(_, idx) => onFlowChange(idx)} variant="scrollable" scrollButtons="auto">
                {flowList.map(flow => (
                  <SubTab key={flow.id} label={flow.colors} />
                ))}
              </Tabs>
            )}

            {revisionCount > 1 && (
              <Card className={classes.marginVertical}>
                <CardHeader title="Estimation Comparison" />
                <Divider />
                <div>
                  {rowsPart.length > 1 && (
                    <MuiTabs value={summaryIdx} onChange={(_, idx) => setSummaryIdx(idx)} variant="scrollable" scrollButtons="auto">
                      {rowsPart.map((part, idx) => (
                        <MuiTab key={idx} label={`${part.color}, ${part.part}`} />
                      ))}
                    </MuiTabs>
                  )}
                  <Divider />
                  <MuiTable columns={columnsSummary} rows={rowsSummaryMap[`${summaryPart?.color}${DEFAULT_SEPARATOR}${summaryPart?.part}`]} />
                  <div className={classes.footer}>
                    <div>
                      <Button variant="outlined" color="primary" label="Finalize Estimation" onClick={() => setOpenFinalizeDialog(true)}>
                        <EditIcon />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <div>
              <div className={clsx({ [classes.card]: revisionCount > 1 })}>
                {revisionList.length > 1 && (
                  <MuiTabs value={revIdx} onChange={(_, idx) => setRevIdx(idx)} indicatorColor={flowList.length > 1 ? "primary" : "secondary"}>
                    {revisionList.map(rev => (
                      <MuiTab idx={rev} label={`Estimation ${rev}`} />
                    ))}
                  </MuiTabs>
                )}

                {revisionCount > 1 && <Divider />}

                <div className={clsx({ [classes.padding]: revisionCount > 1 })}>
                  {/** Estimation Summary */}
                  <Card className={classes.marginVertical}>
                    <CardHeader title="Parts" />
                    <Divider />
                    <div>
                      <MuiTable
                        columns={columnsPart}
                        rows={rowsPart}
                        editable={rowsPart[0]?.totalRevisionNo === revIdx + 1 ? isAdmin || isMerchant || isEstCad : false}
                        onSubmit={onPartSubmit}
                        inlineActions={inlineActions}
                        onInlineActionClick={onInlineActionClick}
                      />
                    </div>
                  </Card>

                  {/** Size Ratio/ Size Breakup */}
                  <Card className={classes.marginVertical}>
                    <CardHeader title={"Size Breakup"} />
                    <Divider />
                    <div>
                      <MuiTable
                        columns={columnsSizeBreakup}
                        rows={rowsSizeBreakup}
                        fontSize={15}
                        editable={revisionList.length <= 1 ? isSizeBreakupEditable && (isAdmin || isMerchant || isEstCad) : false}
                        footerActions={
                          revisionList.length > 1 && rowsPart[0].state === "New" && isSizeBreakupEditable && (isAdmin || isMerchant || isEstCad)
                            ? footerActions
                            : undefined
                        }
                        onFooterActionClick={onUpdateSizeBreakup}
                        onSubmit={onSizeBreakupSubmit}
                        hasRowsChanged={rows =>
                          `${rows.map(({ color, ...row }) => Object.keys(row).reduce((acc, key) => acc + parseInt(row[key]), 0)).join("-")}`
                        }
                      />
                    </div>
                  </Card>

                  {/** Markers */}
                  {partList.map((part, idx) => {
                    const rows = rowsMarker.filter(p => p.part === part);

                    const revNo = revIdx + 1;
                    const revisionDetailList = JSON.parse(activeFlow.revisionDetail);
                    const revisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === part);
                    const shellRevisionDetail = revisionDetailList.find(e => e.revisionNo === revNo && e.part === PART_SHELL);
                    const sizeBreakupList = shellRevisionDetail?.sizeBreakupList || [];
                    const isSizeRatio = !isEmpty(sizeBreakupList) && sizeBreakupList.every(sb => sb.qty <= CUT_OFF_SIZE_RATIO);

                    const cutSetting = revisionDetail?.cutSetting || {};
                    const cutPlanType = cutSetting?.cutPlanType;

                    const isManualCutPlan = cutSetting?.cutPlanType === "CUSTOM";
                    const revState = rows.length === 0 ? EstimationState.NEW : revisionDetail?.state;

                    let editable =
                      !isEmpty(cutPlanType) &&
                      (isManualCutPlan || rows.length > 0) && // For Manual markers or when automated markers are generated
                      EstimationStateValue[revState] <= 2 && // until state is 'Available'
                      (isAdmin || isCutPlanner || isCad);
                    let columns = !isManualCutPlan
                      ? getColumnsMarker(editable).map(({ inputType, ...column }) => ({
                          ...column,
                          inputType: ["length", "efficiency", "remarks"].includes(column.dataKey) ? inputType : undefined
                        }))
                      : getColumnsMarker(editable);
                    columns = !isManualCutPlan ? columns : columns.filter(c => !["qty", "plyCount"].includes(c.dataKey));
                    const attachmentColumns = [
                      ...columns.filter(c => c.dataKey !== "miniMarker"),
                      {
                        dataKey: "attachment",
                        title: "Attachment",
                        inputType: "file-input",
                        options: { accept: { "*/*": [] } }
                      }
                    ].map(column => (column.dataKey !== "attachment" ? { ...column, inputType: "text-field" } : { ...column }));

                    return (
                      <Card key={idx} className={clsx({ [classes.marginVertical]: true })}>
                        <CardHeader title={`Markers ${partList.length > 1 ? ` - ${part}` : ""}`} />
                        <Divider />
                        <div>
                          <MuiTable
                            key={isAttachmentUpload}
                            columns={isAttachmentUpload ? attachmentColumns : columns}
                            rows={rows}
                            editable={editable}
                            editing={isAttachmentUpload}
                            showEditableActions={isManualCutPlan}
                            // actionPlacement="right"
                            variant={isManualCutPlan ? "excel" : "default"}
                            emptyMessage="No Markers available!"
                            fontSize={15}
                            cellLength={100}
                            footerActions={getFooterActions({ cutPlanType, state: revState, isAdmin, isCutPlanner, isCad, rows })}
                            onSubmit={onMarkerSubmit(part)}
                            onFooterActionClick={onFooterActionClick(part, revisionDetail, isSizeRatio)}
                          />
                        </div>
                      </Card>
                    );
                  })}

                  <div className={classes.displayCenter} style={{ padding: "1em", gap: "1em" }}>
                    {(isAdmin || isMerchant || isEstCad) && (
                      <Button label="Revision" variant="outlined" color="primary" onClick={onRevisionClick}>
                        <ActionAdd />
                      </Button>
                    )}
                    {revIdx > 0 && revIdx + 1 === revisionList.length && (isAdmin || isMerchant || isEstCad) && (
                      <Button
                        label="Revision"
                        variant="outlined"
                        color="primary"
                        style={{ color: "red", borderColor: "red" }}
                        onClick={() => onRevisionDelete(activeFlow?.id, revIdx + 1)}>
                        <ActionDelete />
                      </Button>
                    )}
                    <Button label="Print" variant="outlined" color="primary" onClick={onPrint}>
                      <ActionPrint />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PageFooter className={classes.pageFooter}>
            <BackButton variant="contained" color="default" />

            {hasPrivilege(roles, hasAccess, "est_log", "read") && (
              <Button label="History" variant="contained" onClick={() => setDialogState({ active: true, name: DialogName.HISTORY })}>
                <HistoryIcon />
              </Button>
            )}
            {hasPrivilege(roles, hasAccess, "estimation", "delete") && (
              <DeleteButton
                variant="contained"
                recordName="Estimation"
                resource={props.resource}
                basePath={props.basePath}
                record={estimation}
                redirect="home"
              />
            )}
          </PageFooter>
        </div>
      )}
    </div>
  );
};

export default EstimationView;
